// Homepage "Beach of the Week" hero strip. Auto-computed on backend.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Waves, Trophy, Star, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { beachOfWeek } from '@/lib/challenges';
import { listingUrl } from '@/lib/seo';

export const BeachOfWeekStrip = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { beachOfWeek().then(setData).catch(() => setErr(true)); }, []);
  if (err || !data || !data.beach) return null;
  const b = data.beach;
  const r = data.reasons || {};
  return (
    <section className="py-10 bg-gradient-to-br from-sky-50 via-white to-emerald-50" data-testid="beach-of-week">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-sky-700 font-medium mb-3">
          <Waves className="w-4 h-4" /> Beach of the week · picked by locals + weather
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-center bg-white rounded-2xl border border-sky-100 shadow-md overflow-hidden">
          <Link to={listingUrl('beach', b.slug)} className="block aspect-[4/3] md:aspect-auto md:h-full overflow-hidden group relative">
            <img src={b.image} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            {b.image_attribution && (
              <span className="absolute bottom-1 right-1 bg-black/50 text-white/90 text-[9px] leading-tight px-1.5 py-0.5 rounded" data-testid="beach-of-week-credit">
                {b.image_attribution}
              </span>
            )}
          </Link>
          <div className="p-6 md:p-8">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full"><Trophy className="w-3 h-3" /> This week</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mt-2">{b.name}</h2>
            <div className="text-gray-500 text-sm capitalize">{(b.city || '').replace(/-/g, ' ')}</div>
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="inline-flex items-center gap-1 text-yellow-600 font-medium"><Star className="w-4 h-4 fill-current" /> {b.rating}</span>
              {r.reviews && <span className="text-gray-500">{r.reviews} reviews</span>}
              {r.clean_pct != null && <span className="text-emerald-700">✨ {r.clean_pct}% clean</span>}
              {r.water_pct != null && <span className="text-sky-700">💎 {r.water_pct}% clear water</span>}
            </div>
            {r.reports_used > 0 && <div className="text-xs text-gray-400 mt-1">Based on {r.reports_used} recent visitor report{r.reports_used > 1 ? 's' : ''}</div>}
            <Button asChild variant="green" className="mt-5"><Link to={listingUrl('beach', b.slug)} data-testid="beach-of-week-cta">Explore <ArrowRight className="w-4 h-4" /></Link></Button>
          </div>
        </div>
        {data.runners_up?.length > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-3">
            {data.runners_up.map((u) => (
              <Link key={u.id} to={listingUrl('beach', u.slug)} className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-2 hover:border-sky-400 transition" data-testid={`botw-runner-${u.slug}`}>
                <img src={u.image} alt={u.name} className="w-12 h-12 rounded object-cover" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-[11px] text-gray-500">⭐ {u.rating}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
